import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, put, del } from './client';
import type { AppNotification, AppNotificationListResult } from '../types';

export type { AppNotification };

/**
 * 通知查询键。实时帧到达时按前缀整片失效，因此新增键必须挂在 NOTIFICATION_QUERY_KEY 之下，
 * 否则那条缓存不会被推送刷新。
 */
export const NOTIFICATION_QUERY_KEY = ['notifications'] as const;
export const NOTIFICATION_UNREAD_QUERY_KEY = [...NOTIFICATION_QUERY_KEY, 'unread'] as const;

/** 通知列表单页条数 */
export const NOTIFICATION_PAGE_SIZE = 10;

/**
 * 通知列表（无限查询，按页累积）。
 *
 * 此前是 `useGetNotifications(page)` + 组件里自增的 currentPage：点「加载更多」会让
 * 第 2 页整体替换第 1 页，用户翻完一页就找不到上一页的通知了。改成游标式无限查询后
 * 新页追加在尾部，是否还有下一页由后端 pagination 的 page/pageSize/total 判定。
 * 查询键不含页码——失效仍按 NOTIFICATION_QUERY_KEY 前缀整片刷新。
 */
export function useGetNotifications(pageSize: number = NOTIFICATION_PAGE_SIZE, enabled: boolean = true) {
  return useInfiniteQuery({
    queryKey: [...NOTIFICATION_QUERY_KEY, 'list', pageSize],
    queryFn: ({ pageParam }) =>
      get<AppNotificationListResult>('/notifications', { params: { page: pageParam, pageSize } }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const { page, pageSize: size, total } = lastPage.pagination;
      if (!page || !size || total == null) return undefined;
      return page * size < total ? page + 1 : undefined;
    },
    enabled,
    staleTime: 1000 * 60, // 1分钟
    gcTime: 1000 * 60 * 5, // 5分钟
  });
}

/**
 * 未读角标。
 *
 * 这里原先挂着 `refetchInterval: 30000` + `refetchIntervalInBackground: true`：
 * 实时通道上线后轮询整体退场，角标改由 `notification`/`unread` 帧驱动
 * （见 hooks/useNotificationSocket）。断线期间靠重新联网补投、窗口聚焦重取和下拉框里的刷新按钮兜底。
 */
export function useGetUnreadCount(enabled: boolean = true) {
  return useQuery({
    queryKey: NOTIFICATION_UNREAD_QUERY_KEY,
    queryFn: async () => (await get<{ count: number }>('/notifications/unread-count')).count,
    enabled,
    staleTime: 1000 * 5, // 5秒
    gcTime: 1000 * 60 * 5, // 5分钟
  });
}

// 标记通知为已读
export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) => put<AppNotification>(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    }
  });
}

// 标记所有通知为已读
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => put('/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    }
  });
}

// 删除通知
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: number) => del(`/notifications/${notificationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEY });
    }
  });
}
